import React, { MutableRefObject, RefObject, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { IPositionType } from 'src/generated-types'
import { ConfigurePalette } from './configurePalette'
import { centerOf, findPositionsNearCage, toSelectionRect } from './drawingGeometry.action'
import { boundsOf, shapeHitsRect, showMarquee, toSvgPoint } from './drawingSvgElements'

const MARQUEE_MOVE_THRESHOLD_PX = 4
const POSITION_SELECTOR = '[data-cfg-id]'

interface MarqueeDrag {
  startX: number
  startY: number
  endX: number
  endY: number
  startClientX: number
  startClientY: number
  removing: boolean
  moved: boolean
}

interface UseDrawingSelectionOptions {
  hostRef: RefObject<HTMLDivElement>
  positionShapesRef: MutableRefObject<Map<number, SVGGraphicsElement>>
  cageShapesRef: MutableRefObject<Map<number, SVGGraphicsElement>>
  marqueeRef: MutableRefObject<SVGRectElement | null>
  activeType: IPositionType
  typeLabel: string
  palette: ConfigurePalette
  onAdd: (ids: number[]) => void
  onRemove: (ids: number[]) => void
  onToggleGroup: (ids: number[]) => void
}

const getSvgElement = (host: HTMLElement | null) => host?.querySelector('svg') ?? null

export const useDrawingSelection = ({
  hostRef,
  positionShapesRef,
  cageShapesRef,
  marqueeRef,
  activeType,
  typeLabel,
  palette,
  onAdd,
  onRemove,
  onToggleGroup,
}: UseDrawingSelectionOptions) => {
  const { t } = useTranslation('position')
  const dragRef = useRef<MarqueeDrag | null>(null)
  const suppressClickRef = useRef(false)

  const notifyNothingFound = () => toast.info(t('configure-no-area', { ns: 'position', type: typeLabel }))

  const suppressNextClick = () => {
    suppressClickRef.current = true
    window.setTimeout(() => {
      suppressClickRef.current = false
    }, 0)
  }

  const hideMarquee = () => {
    if (marqueeRef.current) marqueeRef.current.style.display = 'none'
  }

  const startMarquee = (event: React.PointerEvent) => {
    if (event.button !== 0 || (event.target as Element).closest?.(POSITION_SELECTOR)) return false
    const svg = getSvgElement(hostRef.current)
    const point = svg && toSvgPoint(svg, event.clientX, event.clientY)
    if (!point) return false
    dragRef.current = {
      startX: point.x,
      startY: point.y,
      endX: point.x,
      endY: point.y,
      startClientX: event.clientX,
      startClientY: event.clientY,
      removing: event.altKey,
      moved: false,
    }
    ;(event.currentTarget as Element).setPointerCapture(event.pointerId)
    return true
  }

  const moveMarquee = (event: React.PointerEvent) => {
    const drag = dragRef.current
    const svg = getSvgElement(hostRef.current)
    if (!drag || !svg) return false
    const point = toSvgPoint(svg, event.clientX, event.clientY)
    if (!point) return true
    drag.endX = point.x
    drag.endY = point.y
    const travelledPx = Math.hypot(event.clientX - drag.startClientX, event.clientY - drag.startClientY)
    if (travelledPx > MARQUEE_MOVE_THRESHOLD_PX) drag.moved = true
    if (marqueeRef.current && drag.moved) {
      const rect = toSelectionRect(drag.startX, drag.startY, drag.endX, drag.endY)
      showMarquee(marqueeRef.current, rect, drag.removing || event.altKey, palette)
    }
    return true
  }

  const endMarquee = (event: React.PointerEvent) => {
    const drag = dragRef.current
    dragRef.current = null
    hideMarquee()
    if (!drag?.moved) return
    suppressNextClick()
    const rect = toSelectionRect(drag.startX, drag.startY, drag.endX, drag.endY)
    const hitIds: number[] = []
    positionShapesRef.current.forEach((shape, positionId) => {
      if (Number(shape.getAttribute('data-cfg-type')) === activeType && shapeHitsRect(shape, rect)) {
        hitIds.push(positionId)
      }
    })
    if (!hitIds.length) {
      notifyNothingFound()
      return
    }
    if (drag.removing || event.altKey) onRemove(hitIds)
    else onAdd(hitIds)
  }

  const cancelMarquee = () => {
    dragRef.current = null
    hideMarquee()
  }

  const getPositionsNearCage = (cageId: number) => {
    const cageShape = cageShapesRef.current.get(cageId)
    const cageBox = cageShape && boundsOf(cageShape)
    if (!cageBox) return []
    const cageCenters = [...cageShapesRef.current.entries()].flatMap(([id, shape]) => {
      const bounds = boundsOf(shape)
      return bounds ? [centerOf(id, bounds)] : []
    })
    const positionCenters = [...positionShapesRef.current.entries()].flatMap(([id, shape]) => {
      const bounds = Number(shape.getAttribute('data-cfg-type')) === activeType ? boundsOf(shape) : null
      return bounds ? [centerOf(id, bounds)] : []
    })
    return findPositionsNearCage(centerOf(cageId, cageBox), cageCenters, positionCenters)
  }

  const selectAroundCage = (cageId: number) => {
    if (activeType === IPositionType.MooringLine) {
      toast.info(t('configure-mooring-cage', { ns: 'position' }))
      return
    }
    const nearbyIds = getPositionsNearCage(cageId)
    if (!nearbyIds.length) {
      notifyNothingFound()
      return
    }
    onToggleGroup(nearbyIds)
  }

  const resolveClick = (event: React.MouseEvent) => {
    if (suppressClickRef.current) return null
    const target = (event.target as Element).closest?.(POSITION_SELECTOR)
    if (!target) return null
    const positionId = Number(target.getAttribute('data-cfg-id'))
    if (Number(target.getAttribute('data-cfg-type')) !== IPositionType.Cage) return positionId
    selectAroundCage(positionId)
    return null
  }

  return { startMarquee, moveMarquee, endMarquee, cancelMarquee, suppressNextClick, resolveClick }
}
